import { useEffect, useRef, useState } from 'react';
import { TEMPLATE_ROW_HIDE_ABOVE_PX, TEMPLATE_ROW_SHOW_BELOW_PX } from './DashboardPromptStarter.constants';

interface UseTemplateRowVisibilityResult {
  isTemplateRowVisible: boolean;
  // A callback ref rather than a RefObject: applying a template remounts the prompt input, so the
  // textarea changes identity. See the effect below.
  promptInputRef: (node: HTMLTextAreaElement | null) => void;
}

// Hides the template gallery once the prompt box has grown tall, and brings it back when the box
// shrinks. Observes the textarea rather than the text: a long paste with no newlines still wraps to
// many lines, and wrapping depends on the container width, so neither the string length nor a
// newline count can answer this. MUI's TextareaAutosize writes the grown height onto the element in
// a layout effect and owns its own resize listener, so one ResizeObserver on it covers typing,
// pasting, container-width changes, zoom and late font loads.
export const useTemplateRowVisibility = (): UseTemplateRowVisibilityResult => {
  // The node lives in state, not a ref, because clicking a template remounts the prompt input to
  // re-seed it. Only a state update re-runs the effect and re-points the observer; a ref plus
  // useEffect([]) would leave it watching a detached textarea and freeze the gallery at whatever
  // verdict it last reached.
  const [promptTextarea, setPromptTextarea] = useState<HTMLTextAreaElement | null>(null);
  const [isTemplateRowVisible, setIsTemplateRowVisible] = useState(true);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!promptTextarea || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ownerWindow = promptTextarea.ownerDocument.defaultView ?? window;

    const resizeObserver = new ResizeObserver(entries => {
      const promptHeight = entries[0]?.contentRect.height ?? 0;

      // Defer out of the observer callback: setting state synchronously here re-enters layout and
      // trips Chrome's "ResizeObserver loop completed with undelivered notifications".
      if (animationFrameRef.current !== null) {
        ownerWindow.cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = ownerWindow.requestAnimationFrame(() => {
        animationFrameRef.current = null;

        // Hysteresis rides in the updater rather than the dependency array, so the observer is
        // never torn down on toggle, React bails out whenever the verdict is unchanged, and the
        // verdict survives a remount instead of snapping back to visible.
        setIsTemplateRowVisible(wasVisible => (
          wasVisible
            ? promptHeight <= TEMPLATE_ROW_HIDE_ABOVE_PX
            : promptHeight < TEMPLATE_ROW_SHOW_BELOW_PX
        ));
      });
    });

    resizeObserver.observe(promptTextarea);

    return () => {
      resizeObserver.disconnect();

      if (animationFrameRef.current !== null) {
        ownerWindow.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [promptTextarea]);

  return { isTemplateRowVisible, promptInputRef: setPromptTextarea };
};
